/**
 * RCK-ASSET-002 | rck-quantity-bundles.js  (PATCHED)
 * Quantity Bundle Discount Widget - JavaScript Controller
 * Prestige Theme v10.7 & v11.0 compatible.
 * Supports both discount_pct (percentage) and discount_gbp (pence) modes.
 * Author tag: RCK
 *
 * Patch notes (RCK-PATCH-2026-04):
 *   - Fixed RCK-JS-010c: removed `_cartQty + tier.qty` arithmetic that was
 *     corrupting the Prestige quantity input after every cart event.
 *   - Hardened RCK-JS-013: _setQtyInput walks all fallback selectors and
 *     also writes `.value` onto the <quantity-input> custom element host.
 *   - Pricing now trusts Liquid: the price passed in via
 *     data-rck-product-price is formatted directly using the currency code
 *     in data-rck-active-currency. NO client-side rate conversion is
 *     applied — on Shopify Markets stores, Liquid already renders the
 *     market-adjusted price, so tier 1 will always match the listed product
 *     price displayed elsewhere on the page. Tier 2+ are calculated from
 *     that same displayed price.
 *   - Removed rckGetCurrencyCtx / Shopify.currency.rate logic and the
 *     currency:changed listener (no longer meaningful without rate math).
 */

/* RCK-JS-001: Guard against double-init */
if (!window.RCKBundleWidget) {

  /* ─── RCK-JS-CONFIG: Single source of truth for all widget constants ─── */
  var RCK_CART_EVENTS = ['cart:updated', 'cart:item-added', 'ajaxProduct:added'];
  var RCK_REINIT_EVENTS = ['shopify:section:load', 'variant:changed', 'productFormUpdated'];
  var RCK_QTY_SELECTORS = [
    'quantity-input input',
    '.product-form input[name="quantity"]',
    'input[name="quantity"]'
  ];
  var RCK_VARIANT_SELECTORS = [
    '.product-form input[name="id"]',
    'form[action*="/cart/add"] input[name="id"]'
  ];

  /* ─── RCK-JS-002: Utility — query first matching element from a list of selectors ─── */
  function rckQueryFirst(selectors, context) {
    context = context || document;
    for (var i = 0; i < selectors.length; i++) {
      var el = context.querySelector(selectors[i]);
      if (el) return el;
    }
    return null;
  }

  /* ─── RCK-JS-FORMAT: Format a subunit (pence / cents) amount in the given
         currency. Works directly on the value Liquid passed us — no rate
         conversion, so tier 1 always matches the listed site price. ─── */
  function rckFormatMoney(cents, currency, locale) {
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat(locale || 'en', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      /* Fallback if the currency code isn't recognised by Intl */
      return currency + ' ' + amount.toFixed(2);
    }
  }

  /* ─── RCK-JS-003: Utility — calculate discount from a tier and base price.
         Cent amounts are in whatever currency Liquid handed us (i.e. the
         active market currency), so arithmetic is a straight subtraction. ─── */
  function rckCalcDiscount(tier, basePrice) {
    var originalTotal = basePrice * tier.qty;
    var discountPct = tier.discount_pct || 0;
    var discountAmount = discountPct > 0
      ? Math.round(originalTotal * (discountPct / 100))
      : (tier.discount_gbp || 0);
    return {
      originalTotal: originalTotal,
      discountAmount: discountAmount,
      discountedTotal: originalTotal - discountAmount,
      hasDiscount: discountAmount > 0,
      discountPct: discountPct
    };
  }

  /* ─── RCK-JS-005: Utility — find best value tier index ─── */
  function rckFindBestIndex(tiers) {
    return tiers.reduce(function(best, tier, i) {
      var thisDiscount = tier.discount_pct || tier.discount_gbp || 0;
      var bestDiscount = tiers[best].discount_pct || tiers[best].discount_gbp || 0;
      return thisDiscount > bestDiscount ? i : best;
    }, 0);
  }

  /* ─── RCK-JS-006: Utility — render a single tier card as an HTML string ─── */
  function rckRenderTierHTML(tier, index, isBest, basePrice, widgetId, currency, locale) {
    var calc = rckCalcDiscount(tier, basePrice);
    var discountLabel = calc.discountPct > 0
      ? 'Save ' + calc.discountPct + '%'
      : 'Save ' + rckFormatMoney(calc.discountAmount, currency, locale);

    var priceContent = calc.hasDiscount
      ? '<div class="rck-bundle-tier__qty-row">' +
          '<span class="rck-bundle-tier__qty">' + tier.label + '</span>' +
          '<span class="rck-bundle-tier__discount">' + discountLabel + '</span>' +
        '</div>' +
        '<div class="rck-bundle-tier__price-row">' +
          '<span class="rck-bundle-tier__new-price">' + rckFormatMoney(calc.discountedTotal, currency, locale) + '</span>' +
          '<span class="rck-bundle-tier__original-price">' + rckFormatMoney(calc.originalTotal, currency, locale) + '</span>' +
        '</div>'
      : '<span class="rck-bundle-tier__qty">' + tier.label + '</span>' +
        '<div class="rck-bundle-tier__price-row">' +
          '<span class="rck-bundle-tier__new-price">' + rckFormatMoney(calc.originalTotal, currency, locale) + '</span>' +
        '</div>';

    return '<label class="rck-bundle-tier' + (isBest ? ' rck-bundle-tier--best' : '') + '" data-rck-tier="' + index + '">' +
      '<input type="radio"' +
        ' name="rck_bundle_qty_' + widgetId + '"' +
        ' value="' + tier.qty + '"' +
        ' data-rck-tier-index="' + index + '"' +
        (index === 0 ? ' checked' : '') +
      '>' +
      (isBest ? '<span class="rck-bundle-tier__badge">Best Value</span>' : '') +
      priceContent +
    '</label>';
  }

  /* ─── RCK-JS-007: Utility — render summary line ─── */
  function rckRenderSummary(tier, basePrice, currency, locale) {
    var calc = rckCalcDiscount(tier, basePrice);
    if (!calc.hasDiscount) return '';
    return 'You save <strong>' + rckFormatMoney(calc.discountAmount, currency, locale) + '</strong>' +
           ' \u2014 total <strong>' + rckFormatMoney(calc.discountedTotal, currency, locale) + '</strong>';
  }

  /* ═══════════════════════════════════════════════════════════════════════
     RCK-JS-008: Main widget class
     Single Responsibility: manages one widget instance — state, render, events
  ═══════════════════════════════════════════════════════════════════════ */
  class RCKBundleWidget {

    constructor(el) {
      this.el             = el;
      this.dataset        = el.dataset;
      this.id             = el.id;
      this._tiers         = [];
      this._basePrice     = 0;
      this._selectedIndex = 0;
      this._currency      = 'GBP';
      this._locale        = 'en';
    }

    /* RCK-JS-008a: Parse config and boot */
    init() {
      var config;
      try {
        config = JSON.parse(this.dataset.rckConfig || '{}');
      } catch (e) {
        console.warn('[RCK Bundle] Invalid JSON config:', e);
        return;
      }

      if (!config.enabled || !Array.isArray(config.tiers) || config.tiers.length === 0) return;

      this._tiers     = config.tiers;
      this._basePrice = parseInt(this.dataset.rckProductPrice || 0, 10);
      this._currency  = this.dataset.rckActiveCurrency || 'GBP';
      this._locale    = this.dataset.rckLocale || 'en';

      this._renderTiers();
      this._bindEvents();
      this._selectTier(0);
    }

    /* RCK-JS-009: Render all tier cards */
    _renderTiers() {
      var container = this.el.querySelector('.rck-bundle-widget__tiers');
      if (!container) return;

      var bestIndex = rckFindBestIndex(this._tiers);
      var currency  = this._currency;
      var locale    = this._locale;

      container.innerHTML = this._tiers.map(function(tier, i) {
        var isBest = i === bestIndex && (tier.discount_pct || tier.discount_gbp || 0) > 0;
        return rckRenderTierHTML(tier, i, isBest, this._basePrice, this.id, currency, locale);
      }.bind(this)).join('');
    }

    /* RCK-JS-010: Bind all event listeners */
    _bindEvents() {
      var self = this;

      /* RCK-JS-010a: Tier selection via radio change */
      this.el.addEventListener('change', function(e) {
        if (e.target && e.target.type === 'radio') {
          self._selectTier(parseInt(e.target.getAttribute('data-rck-tier-index'), 10));
        }
      });

      /* RCK-JS-010b: Tier selection via label click */
      this.el.addEventListener('click', function(e) {
        var label = e.target.closest('[data-rck-tier]');
        if (label) {
          var idx = parseInt(label.getAttribute('data-rck-tier'), 10);
          if (!isNaN(idx)) self._selectTier(idx);
        }
      });

      /* RCK-JS-010c PATCHED: On cart update, re-sync the Prestige quantity
         input to the currently selected tier (no more compounding). */
      function onCartUpdate() {
        var tier = self._tiers[self._selectedIndex];
        if (tier) self._setQtyInput(tier.qty);
      }

      RCK_CART_EVENTS.forEach(function(event) {
        document.addEventListener(event, onCartUpdate);
      });
    }

    /* RCK-JS-011: Select a tier by index */
    _selectTier(index) {
      this._selectedIndex = index;
      var tier = this._tiers[index];
      if (!tier) return;

      this.el.querySelectorAll('.rck-bundle-tier').forEach(function(el, i) {
        el.classList.toggle('rck-bundle-tier--selected', i === index);
      });

      var radio = this.el.querySelector('input[data-rck-tier-index="' + index + '"]');
      if (radio) radio.checked = true;

      this._setQtyInput(tier.qty);
      this._updateSummary(tier);
    }

    /* RCK-JS-013 PATCHED: Update the DOM quantity input. */
    _setQtyInput(qty) {
      var section = this.el.closest('[data-section-id]') || this.el.closest('section') || document;
      var qtyInput = null;
      for (var i = 0; i < RCK_QTY_SELECTORS.length; i++) {
        qtyInput = section.querySelector(RCK_QTY_SELECTORS[i]);
        if (qtyInput) break;
      }
      if (!qtyInput) qtyInput = rckQueryFirst(RCK_QTY_SELECTORS);
      if (!qtyInput) return;

      qtyInput.value = qty;

      var ceHost = qtyInput.closest('quantity-input');
      if (ceHost) {
        try { ceHost.value = qty; } catch (e) { /* ignore */ }
      }

      qtyInput.dispatchEvent(new Event('input',  { bubbles: true }));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* RCK-JS-014: Update the saving summary line */
    _updateSummary(tier) {
      var summary = this.el.querySelector('.rck-bundle-widget__saving-summary');
      if (summary) summary.innerHTML = rckRenderSummary(tier, this._basePrice, this._currency, this._locale);
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     RCK-JS-015: Initialisation — find and boot all widget instances
  ═══════════════════════════════════════════════════════════════════════ */
  window.RCKBundleWidget = RCKBundleWidget;

  function rckInitWidgets() {
    document.querySelectorAll('[data-rck-bundle]:not([data-rck-initialized])').forEach(function(el) {
      el.setAttribute('data-rck-initialized', 'true');
      new RCKBundleWidget(el).init();
    });
  }

  /* RCK-JS-015a: Boot on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rckInitWidgets);
  } else {
    rckInitWidgets();
  }

  /* RCK-JS-015b: Re-init on theme/variant events */
  RCK_REINIT_EVENTS.forEach(function(event) {
    document.addEventListener(event, rckInitWidgets);
  });

  /* RCK-JS-015c: Re-init via MutationObserver as catch-all fallback */
  new MutationObserver(function(mutations) {
    var shouldInit = mutations.some(function(mutation) {
      return Array.from(mutation.addedNodes).some(function(node) {
        return node.nodeType === 1 && (
          (node.hasAttribute && node.hasAttribute('data-rck-bundle')) ||
          (node.querySelector && node.querySelector('[data-rck-bundle]'))
        );
      });
    });
    if (shouldInit) rckInitWidgets();
  }).observe(document.body, { childList: true, subtree: true });

}